import { KeyValue, KeyValueDefault } from '@/main/models/common';
import {
  getTranslateByKey,
  isEmpty,
  isKeyValueDefault,
  isNullUndefined
} from '@/main/services/utils';
import { uppercaseFirstLetter } from '@/main/services/utilsString';
import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Autocomplete,
  BaseSelectProps,
  FilterOptionsState,
  Paper,
  PaperProps,
  useMediaQuery,
  useTheme
} from '@mui/material';
import Popper, { PopperProps } from '@mui/material/Popper';
import React, { useEffect, useRef, useState } from 'react';
import {
  Controller,
  FieldError,
  RegisterOptions,
  UseFormSetValue
} from 'react-hook-form';
import KogInputText from '../inputText/inputText';
import { Spinner, useStyles } from './styles';

type IAutoCompleteProps = {
  label?: string;
  control: any;
  isLoading?: boolean;
  options: (KeyValue | KeyValueDefault)[] | null;
  onChangeCustom: (valueSelected: KeyValue | KeyValueDefault | null) => void;
  confirmChange?: (
    valueSelected: KeyValue | KeyValueDefault | null
  ) => Promise<boolean>;
  hasErrorCustom?: boolean;
  errorMessageCustom?: string;
  rulesCustom?: { required: boolean } & RegisterOptions;
  nameCustom: string;
  autoHighlightCustom?: boolean;
  setValueCustom?: UseFormSetValue<any>;
  valueDefaultKey?: string | number | null;
  isDefaultValueUnique?: boolean;
  disabledMessage?: boolean;
  disabled?: boolean;
  autoOnChangeCustom?: boolean;
  hideValue?: boolean;
  isPluralLabel?: boolean;
  disableClearable?: boolean;
  disablePortal?: boolean;
  placeholder?: string;
  filterOptions?: (
    options: (KeyValue | KeyValueDefault)[],
    state: FilterOptionsState<KeyValue | KeyValueDefault>
  ) => (KeyValue | KeyValueDefault)[];
} & BaseSelectProps;

const CustomPaper = (props: PaperProps) => {
  return (
    <Paper
      elevation={8}
      {...props}
      className={`${props.className} ignore-click`}
    />
  );
};

const CustomPopper = (props: PopperProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: isMobile ? 'auto' : props.style?.width,
        minWidth: isMobile ? (props.style?.width as string) : undefined
      }}
    />
  );
};
function KogAutoCompleteControlled({
  label,
  control,
  isLoading,
  options,
  onChangeCustom,
  hasErrorCustom,
  errorMessageCustom,
  nameCustom,
  rulesCustom = { required: false },
  autoHighlightCustom,
  setValueCustom,
  valueDefaultKey,
  isDefaultValueUnique = false, // Prop to set the default value when the options are equal to 1, important not to break old components
  disabledMessage = false,
  disabled = false,
  hideValue = false,
  autoOnChangeCustom = false,
  isPluralLabel = false,
  variant,
  autoFocus,
  ...props
}: IAutoCompleteProps) {
  const classes = useStyles();
  const getOptions = () => (options !== null ? options : []);
  const optionsDrop = getOptions();
  const renderRef = useRef(false);
  const [openAutocomplete, setOpenAutocomplete] = useState(false);

  const renderErrorMessages = (error: FieldError | undefined) => {
    const requiredMessage = getTranslateByKey(
      !isNullUndefined(isPluralLabel) && isPluralLabel
        ? 'information.areRequired'
        : 'information.isRequired',
      true
    );
    const fieldMessage = getTranslateByKey('information.field');

    if (!error) return null;
    switch (error.type) {
      case 'required':
        return uppercaseFirstLetter(
          requiredMessage.replace('{0}', `${label || fieldMessage}`)
        );
      default:
        return error.message;
    }
  };

  // UseEffect's
  useEffect(() => {
    if (autoHighlightCustom && setValueCustom && optionsDrop.length > 0) {
      const firstOption = optionsDrop[0];
      setValueCustom(nameCustom, firstOption);
    }
  }, [autoHighlightCustom]);

  useEffect(() => {
    if (
      !isLoading &&
      optionsDrop.length > 0 &&
      !isEmpty(valueDefaultKey) &&
      setValueCustom &&
      !renderRef.current
    ) {
      const findValue = optionsDrop.find(option =>
        isKeyValueDefault(option)
          ? String(option.default) === String(valueDefaultKey) ||
          option.default === 'True'
          : String(option.key) === String(valueDefaultKey)
      );
      setValueCustom(nameCustom, findValue);
      renderRef.current = true;
    }
  }, [isLoading, valueDefaultKey, options]);

  useEffect(() => {
    if (optionsDrop.length === 1 && setValueCustom && isDefaultValueUnique) {
      setValueCustom(nameCustom, optionsDrop[0]);
      if (autoOnChangeCustom) {
        onChangeCustom(optionsDrop[0]);
      }
    }
  }, [options, isDefaultValueUnique]);

  return (
    <Controller
      control={control}
      name={nameCustom}
      rules={rulesCustom}
      defaultValue={null}
      render={({ field: { onChange, value }, fieldState }) => {
        return (
          <Autocomplete
            open={openAutocomplete}
            openOnFocus
            popupIcon={isLoading ? <Spinner /> : <KeyboardArrowDown />}
            className={`${classes.inputAutoComplete} ignore-click`}
            size="small"
            loading={isLoading}
            disabled={isLoading || disabled}
            onChange={async (_, optionSelected) => {
              const selected = optionSelected as
                | KeyValue
                | KeyValueDefault
                | null;
              if (props.confirmChange) {
                const isConfirmed = await props.confirmChange(selected);
                if (!isConfirmed) return;
              }
              onChange(optionSelected);
              onChangeCustom(selected);
            }}
            onInputChange={(
              _: React.ChangeEvent<{}>,
              newInputValue: string,
              reason: string
            ) => {
              if (reason === 'clear') setOpenAutocomplete(false);
              if (newInputValue === '') setOpenAutocomplete(false);
            }}
            PaperComponent={CustomPaper}
            PopperComponent={CustomPopper}
            value={hideValue ? null : (value ?? null)}
            options={getOptions()}
            getOptionKey={(option: KeyValue | KeyValueDefault) => {
              if ('key' in option && option.key) return String(option.key);
              if (isKeyValueDefault(option)) return String(option.default);
              return String(option.value);
            }}
            getOptionLabel={(option: KeyValue | KeyValueDefault) =>
              option?.value || ''
            }
            renderInput={params => (
              <KogInputText
                label={label}
                title={value?.value ?? ''}
                variant={variant ?? 'filled'}
                error={!!hasErrorCustom || !!fieldState.error}
                helperText={
                  !disabledMessage &&
                  (errorMessageCustom || renderErrorMessages(fieldState.error))
                }
                required={rulesCustom?.required}
                autoFocus={autoFocus}
                {...params}
                placeholder={props.placeholder}
              />
            )}
            onOpen={() => setOpenAutocomplete(true)}
            onClose={() => setOpenAutocomplete(false)}
            {...props}
          />
        );
      }}
    />
  );
}

export default KogAutoCompleteControlled;
